import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { CoinType } from '../../../../models/CoinType';
import { CoinTier } from '../../../../models/CoinTier';
import { loadCoinTypes, addCoinType, updateCoinType } from '../../../../state/coin-type/coin-type.actions';
import { selectAllCoinTypes } from '../../../../state/coin-type/coin-type.selectors';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { NgFor, NgIf, AsyncPipe, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NewCointypeDialogComponent } from './new-cointype-dialog/new-cointype-dialog.component';
import { PageHeaderComponent } from '../../../page-header/page-header.component';

@Component({
  selector: 'app-manage-coin-tiering',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatTableModule,
    MatCardModule,
    NgFor,
    NgIf,
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
  MatInputModule,
  MatDialogModule,
  PageHeaderComponent,
  ],
  templateUrl: './manage-coin-tiering.component.html',
  styleUrls: ['./manage-coin-tiering.component.css']
})
export class ManageCoinTieringComponent implements OnInit {
  coinTypes$: Observable<CoinType[]>;
  selectedCoinType: CoinType | null = null;
  selectedCoinTypeId: number | null = null;
  private initialSelectionDone = false;
  addCoinTypeForm: FormGroup;
  showAddCoinType = false;

  constructor(private store: Store, private fb: FormBuilder, private dialog: MatDialog) {
    this.coinTypes$ = this.store.select(selectAllCoinTypes);
    this.coinTypes$.subscribe(coinTypes => {
      const sorted = this.getSortedCoinTypes(coinTypes);
      if (!this.initialSelectionDone && sorted.length > 0) {
        this.selectedCoinTypeId = sorted[0].id ?? null;
        // Always sort tiers ascending by breakPoint
        const first = { ...sorted[0], tiers: [...(sorted[0].tiers || [])].sort((a, b) => a.breakPoint - b.breakPoint) };
        this.selectedCoinType = first;
        this.initialSelectionDone = true;
      }
    });
    this.addCoinTypeForm = this.fb.group({
      value: [null, Validators.required],
      tiers: this.fb.array([])
    });
  }

  ngOnInit() {
    this.store.dispatch(loadCoinTypes());
  }

  onCoinTypeChange(coinTypeId: number) {
    this.selectedCoinTypeId = coinTypeId;
    this.coinTypes$.subscribe(coinTypes => {
      const sorted = this.getSortedCoinTypes(coinTypes);
      const found = sorted.find(ct => ct.id === coinTypeId) || null;
        if (found) {
          this.selectedCoinType = { ...found, tiers: [...(found.tiers || [])].sort((a, b) => a.breakPoint - b.breakPoint) };
        } else {
          this.selectedCoinType = null;
        }
    }).unsubscribe();
  }
  get tiers(): FormArray {
    return this.addCoinTypeForm.get('tiers') as FormArray;
  }

  addTier() {
    this.tiers.push(this.fb.group({
      breakPoint: [null, Validators.required],
      amount: [null, Validators.required]
    }));
  }

  removeTier(index: number) {
    this.tiers.removeAt(index);
  }


  submitAddCoinType() {
    if (this.addCoinTypeForm.valid) {
      const { value, tiers, baseAmount } = this.addCoinTypeForm.value;
      const newCoinType = {
        value,
        activeAt: new Date(),
        tiers,
        baseAmount: baseAmount
      };
      this.store.dispatch(addCoinType({ coinType: newCoinType }));
      this.addCoinTypeForm.reset();
      this.tiers.clear();
      this.showAddCoinType = false;
    }
  }

  openNewCoinTypeDialog() {
    const dialogRef = this.dialog.open(NewCointypeDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Always include CoinTier ids in the CoinType object
        const tiersWithIds = (result.tiers || []).map((tier: any, i: number) => ({ ...tier, id: tier.id ?? undefined }));
        const coinTypeWithTiers = { ...result, tiers: tiersWithIds };
        this.store.dispatch(addCoinType({ coinType: coinTypeWithTiers }));
      }
    });
  }

  openEditCoinTypeDialog() {
    if (!this.selectedCoinType) return;
    const dialogRef = this.dialog.open(NewCointypeDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { coinType: this.selectedCoinType }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Always include CoinTier ids in the CoinType object
        const tiersWithIds = (result.tiers || []).map((tier: any, i: number) => ({ ...tier, id: tier.id ?? undefined }));
        const updated = { ...this.selectedCoinType, ...result, tiers: tiersWithIds };
        console.log('Updating CoinType:', updated);
        this.store.dispatch(updateCoinType({ coinType: updated }));
        // Update selection to reflect the new value in the dropdown
        // Always sort tiers ascending by breakPoint after update
        this.selectedCoinType = {
          ...updated,
          tiers: [...(updated.tiers || [])].sort((a, b) => a.breakPoint - b.breakPoint)
        };
        this.selectedCoinTypeId = updated.id ?? this.selectedCoinTypeId;
      }
    });
  }

  getSortedCoinTypes(coinTypes: CoinType[]): CoinType[] {
    return [...coinTypes].sort((a, b) => new Date(b.activeAt).getTime() - new Date(a.activeAt).getTime());
  }

  back() {
    window.history.back();
  }
}
